import { Button, Icon, Tooltip } from '@zoom/components';
import classnames from 'classnames';
import React, { useContext, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { BOTTOM_PANEL_CLOSE_BUTTON } from './constants';

export const defaultBottomPanelValues = {
  isBottomPanelOpen: false,
  toggleBottomPanel: () => null,
  openBottomPanel: () => null,
  closeBottomPanel: () => null,
};

export const BottomPanelContext = React.createContext<{
  isBottomPanelOpen: boolean;
  toggleBottomPanel: () => void;
  openBottomPanel: () => void;
  closeBottomPanel: () => void;
}>(defaultBottomPanelValues);

export const BottomPanelProvider: React.FC<React.PropsWithChildren> = ({ children }) => {
  const [isBottomPanelOpen, setBottomPanelOpen] = useState(false);

  const openBottomPanel = () => setBottomPanelOpen(true);

  const closeBottomPanel = () => setBottomPanelOpen(false);

  const toggleBottomPanel = () => setBottomPanelOpen((open) => !open);

  return (
    <BottomPanelContext.Provider
      value={{
        isBottomPanelOpen,
        toggleBottomPanel,
        openBottomPanel,
        closeBottomPanel,
      }}
    >
      {children}
    </BottomPanelContext.Provider>
  );
};

export const BottomPanelConsumer = BottomPanelContext.Consumer;

export const useBottomPanel = () => useContext(BottomPanelContext);

interface BottomPanelContainerProps extends React.PropsWithChildren {
  forwardedRef?: React.RefObject<HTMLDivElement>;
}

export const BottomPanelContainer: React.FC<BottomPanelContainerProps> = ({ forwardedRef, children }) => (
  <aside className="bottom-panel-container" ref={forwardedRef}>
    {children}
  </aside>
);

interface BottomPanelProps extends React.PropsWithChildren {
  tag?: React.ElementType;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  [name: string]: any;
}

export const BottomPanel: React.FC<BottomPanelProps> = ({ children, className, tag: Tag = 'div', ...props }) => (
  <Tag {...props} className={classnames('bottom-panel', className)}>
    {children}
  </Tag>
);

export interface BottomPanelHeaderProps extends React.PropsWithChildren {
  onClose?: () => void;
}

export const BottomPanelHeader: React.FC<BottomPanelHeaderProps> = ({ children, onClose }) => {
  const { closeBottomPanel } = useBottomPanel();
  const { t } = useTranslation();

  const handleClose = () => {
    if (onClose) {
      onClose();
    }

    closeBottomPanel();
  };

  return (
    <div className="bottom-panel__header">
      <div className="mr-auto">{children}</div>
      <Tooltip message={t('layout.action.close')} placement="bottom">
        <Button
          aria-label={t('layout.action.close')}
          className="bottom-panel__close-button"
          data-test-id={BOTTOM_PANEL_CLOSE_BUTTON}
          kind="info"
          onClick={handleClose}
        >
          <Icon color="red" kind="cross" size={12} />
        </Button>
      </Tooltip>
    </div>
  );
};

interface BottomPanelBodyProps extends React.PropsWithChildren {
  className?: string;
}

export const BottomPanelBody: React.FC<BottomPanelBodyProps> = ({ className, children }) => (
  <div className={className}>{children}</div>
);
